/* Sura — Frequently Bought Together (<sura-fbt>) */
if (!customElements.get('sura-fbt')) {
  class SuraFbt extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('.sura-fbt__panel'));

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => this.onTabKey(e, i));
      });

      this.panels.forEach((panel) => {
        panel.addEventListener('change', (e) => {
          if (e.target.matches('.sura-fbt__check')) this.update(panel);
        });
        const btn = panel.querySelector('[data-add]');
        if (btn) btn.addEventListener('click', () => this.add(panel));
        this.update(panel);
      });

      // Theme editor: show the panel for the block being edited
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.panels.findIndex((p) => p.contains(e.target) || p === e.target);
        const t = this.tabs.findIndex((tab) => tab === e.target);
        if (i > -1) this.select(i);
        else if (t > -1) this.select(t);
      });
    }

    /* ---------- Tabs ---------- */
    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        if (on && focus) tab.focus();
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    }

    onTabKey(e, i) {
      const last = this.tabs.length - 1;
      let next;
      if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      else return;
      e.preventDefault();
      this.select(next, true);
    }

    /* ---------- Totals ---------- */
    state(panel) {
      const all = Array.from(panel.querySelectorAll('.sura-fbt__check'));
      const selected = all.filter((c) => c.checked && !c.disabled);
      const discount = parseInt(panel.dataset.discount, 10) || 0;
      // Discount applies only to the complete combo, every book available and selected
      const complete = all.length > 1 && selected.length === all.length;
      const sum = selected.reduce((a, c) => a + Number(c.dataset.price), 0);
      const compare = selected.reduce((a, c) => a + Number(c.dataset.compare), 0);
      const total = complete && discount > 0 ? Math.floor((sum * (100 - discount)) / 100) : sum;
      return { all, selected, discount, complete, total, compare };
    }

    update(panel) {
      const { all, selected, discount, complete, total, compare } = this.state(panel);

      all.forEach((c) => {
        c.closest('.sura-fbt__item')?.classList.toggle('is-deselected', !c.checked && !c.disabled);
      });

      const totalEl = panel.querySelector('[data-total]');
      const compareEl = panel.querySelector('[data-compare-total]');
      const saveEl = panel.querySelector('[data-save]');
      const noteEl = panel.querySelector('[data-note]');
      const btn = panel.querySelector('[data-add]');

      this.setMoney(totalEl, total, 'Combo price');
      this.setMoney(compareEl, compare, 'Regular price');
      compareEl.hidden = !(compare > total);

      const save = compare > 0 ? Math.floor(((compare - total) * 100) / compare) : 0;
      saveEl.textContent = `Save ${save}%`;
      saveEl.hidden = save < 1;

      const anySoldOut = all.some((c) => c.disabled);
      if (discount > 0 && !complete && !anySoldOut) {
        noteEl.textContent = `Select all ${all.length} books to get ${discount}% off`;
        noteEl.hidden = false;
      } else {
        noteEl.hidden = true;
      }

      btn.disabled = selected.length === 0;
    }

    setMoney(el, cents, label) {
      if (!el) return;
      el.innerHTML = `<span class="visually-hidden">${label}</span>${this.formatMoney(cents)}`;
    }

    formatMoney(cents) {
      const fmt = this.dataset.moneyFormat || '₹{{amount}}';
      const rules = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
      };
      const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        const [dp, thou, dec] = rules[key] || rules.amount;
        const [int, frac] = (cents / 100).toFixed(dp).split('.');
        const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
        return frac && Number(frac) !== 0 ? grouped + dec + frac : grouped; // mirrors money_without_trailing_zeros
      });
      const tmp = document.createElement('textarea'); // decode entities such as &#8377;
      tmp.innerHTML = out;
      return tmp.value;
    }

    /* ---------- Cart ---------- */
    async add(panel) {
      const { selected, complete } = this.state(panel);
      if (!selected.length) return;

      const btn = panel.querySelector('[data-add]');
      const label = panel.querySelector('[data-add-label]');
      const status = panel.querySelector('[data-status]');
      const original = label.textContent;
      const combo = panel.dataset.combo;

      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      label.textContent = 'Adding…';
      status.textContent = '';
      status.classList.remove('is-error');

      const items = selected.map((c) => ({
        id: Number(c.value),
        quantity: 1,
        // Underscore = hidden at checkout; lets a Function or report identify combo lines later
        properties: complete ? { _sura_combo: combo } : {},
      }));

      try {
        const res = await fetch(this.dataset.addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add these books to the cart.');

        const code = panel.dataset.code;
        if (complete && code) {
          await fetch(this.dataset.updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code }),
          }).catch(() => {});
        }

        label.textContent = 'Added to cart';
        status.textContent = `${selected.length} ${selected.length === 1 ? 'book' : 'books'} added to your cart.`;
        this.dispatchEvent(new CustomEvent('sura:cart:added', { bubbles: true, detail: { items: data.items, combo } }));

        if (this.dataset.afterAdd === 'cart') {
          window.location.href = this.dataset.cartUrl;
          return;
        }
        this.refreshThemeCart(data);
      } catch (err) {
        status.textContent = err.message;
        status.classList.add('is-error');
      } finally {
        btn.removeAttribute('aria-busy');
        setTimeout(() => {
          label.textContent = original;
          this.update(panel);
        }, 1800);
      }
    }

    /**
     * Hook for the Minimog cart drawer / header count.
     * Wire this to the theme's own cart-refresh method once confirmed in the
     * vendor JS; until then a generic document event is fired.
     */
    refreshThemeCart(data) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
    }
  }

  customElements.define('sura-fbt', SuraFbt);
}
